import React, { useMemo } from 'react';
import { UpcomingAppoinment } from '../../interfaces';
import { AppointmentHasuraStatus } from '../../const/appointment';
import { twMerge } from 'tailwind-merge';
import CustomTooltip from 'components/CustomTooltip';
import OptionsModal from 'components/optionsModal/OptionsModal';
import { useAppointmentCardHelper } from './helper/useAppointmentCardHelper';
import { ReactComponent as DnaVisitTypeIcon } from 'assets/icons/appointmentsIcons/dnaVisitTypeIconTable.svg';
import { ReactComponent as Clock } from 'assets/icons/time.svg';
import { ReactComponent as Reason } from 'assets/icons/appointmentsIcons/reason-goal.svg';
import { ReactComponent as GoogleCalendarIcon } from 'assets/icons/google-calendar.svg';
import { ReactComponent as OutlookIcon } from 'assets/icons/outlook-icon.svg';
import DateAppointment from './DateAppointment';
import IconRow from './IconRow';
import { getUserBrowserTimeZone } from 'utilities/functions';
import { getPeriodString } from './StartTimeAndTypeappointment';
import { sanitize } from 'utilities/sanitizer';
import ButtonComponent from 'components/button/buttonComponent';
import { useNavigate } from 'react-router-dom';
import { SCHEDULE_DNA_VISIT } from 'utilities/routes';
import { mapUpcomingDnaAppointmentToCalendarParams } from 'utilities/mapAppointmentToCalendarParams';
import { useAddToCalendar } from 'hooks/useAddToCalendar';

interface IUpcomingAppointmentCard extends UpcomingAppoinment {
  appointmentStatus: AppointmentHasuraStatus;
}

const UpcomingDnaAppointmentCardDesktop: React.FC<IUpcomingAppointmentCard> = ({
  appointmentStatus,
  ...appointment
}) => {
  const navigate = useNavigate();
  const { addToGoogleCalendar, addToOutlookCalendar } = useAddToCalendar();

  const {
    date,
    end,
    status,
    name,
    appointmentType,
    appointmentId,
    providerId,
    patientId,
    appointmentUrl,
  } = appointment;

  const {
    locale,
    label,
    isNotPaid,
    isJoinable,
    statusClassName,
    notification,
    opened,
    setOpened,
    hasPastTimeCancelAppointment,
  } = useAppointmentCardHelper({
    date,
    appointmentStatus,
    end,
    status,
    appointmentId,
    appointmentType,
    providerId,
    patientId,
    name,
  });

  const options = useMemo(
    () => [
      {
        Icon: GoogleCalendarIcon,
        label: locale?.addToGoogleCalendar || 'Add to Google calendar',
        onClick: () =>
          addToGoogleCalendar(
            mapUpcomingDnaAppointmentToCalendarParams(appointment),
          ),
      },
      {
        Icon: OutlookIcon,
        label: locale?.addToOutlookCalendar || 'Add to Outlook calendar',
        onClick: () =>
          addToOutlookCalendar(
            mapUpcomingDnaAppointmentToCalendarParams(appointment),
          ),
      },
    ],
    [addToGoogleCalendar, addToOutlookCalendar, locale, appointment],
  );

  const handleJoin = () =>
    window.open(sanitize(appointmentUrl ?? ''), '_blank');
  const handleDetailsClick = () => navigate(SCHEDULE_DNA_VISIT);

  const tz = getUserBrowserTimeZone();

  return (
    <div
      className="max-desktop:hidden w-full"
      data-testid="upcoming-appointment-card-desktop"
    >
      <div className="flex items-center desktop:px-30 px-5 py-4 border border-transparent border-b-gray-opacity-15 bg-neutral-100 rounded-tr-xl rounded-tl-xl">
        <DnaVisitTypeIcon className="w-6 mr-1" />
        <span className="text-title font-semibold text-neutral-800 whitespace-nowrap">
          DNA appointment with Dr. {name}
        </span>
        <span
          className={twMerge('mx-3 capitalize', statusClassName)}
          data-testid={`appointment-status-${status}`}
          data-paid-status={!isNotPaid}
        >
          {label || status}
        </span>

        <CustomTooltip
          id={`status-${appointmentId}`}
          content={notification}
          place="bottom"
        />

        {!hasPastTimeCancelAppointment && (
          <div className="ml-auto">
            <OptionsModal
              opened={opened}
              setOpened={setOpened}
              options={options}
              className="!bg-transparent !border-transparent -mr-5"
              optionsMenuClassName="rounded-md w-[17rem] !border-transparent -left-[14rem]"
              optionsClassName="uppercase text-clc-blue text-sm font-bold whitespace-nowrap"
            />
          </div>
        )}
      </div>
      <div className="grid grid-cols-9 gap-30 p-5 desktop:px-30 pb-5 pt-2.5 border-b-gray-opacity-15 bg-neutral-100 rounded-br-xl rounded-bl-xl">
        <div className="col-span-1">
          <DateAppointment startDate={date} />
        </div>
        <div className="col-span-3 flex justify-start">
          <IconRow
            icon={<Clock className="fill-neutral-800" />}
            text={`${getPeriodString(date, end)} ${tz}`}
            testId={`start-datetime-${date}`}
          />
        </div>
        <div className="col-span-3 flex justify-start">
          <IconRow
            icon={<Reason className="min-w-[1.25rem]" />}
            text="DNA results overview"
          />
        </div>
        <div className="col-span-2 flex justify-end">
          {handleDetailsClick && (
            <ButtonComponent
              type="underline"
              className="!text-sm rounded max-w-[6.75rem] !w-fit !px-0 ml-auto block"
              desktopFullWidth
              testID={'dna-detail'}
              onClick={handleDetailsClick}
            >
              {locale?.details || 'Details'}
            </ButtonComponent>
          )}
          {isJoinable && (
            <ButtonComponent
              className="!text-sm rounded min-w-fit max-w-[6.75rem] !px-3 ml-auto block"
              desktopFullWidth
              testID={'join-appointment'}
              onClick={handleJoin}
            >
              {locale?.joinNow}
            </ButtonComponent>
          )}
        </div>
      </div>
    </div>
  );
};

export default UpcomingDnaAppointmentCardDesktop;
